import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ISessionState } from './session-state';
import { StateSlice } from '../state-slice';
import { User } from '@core/models/user/user';
import { DatetimeFormat } from '@core/models/common/datetime-format';
import { Skill } from '@core/models/common/skill';
import { sortAlphabeticalByProperty } from '@core/utils/sorting';
import { Company } from '@core/models/common/company';
import { getUserDatetimeFormats as getUserDatetimeOptions } from '../global/global-selectors';

export const selectSessionState = createFeatureSelector<ISessionState>(StateSlice.SESSION);


export const getSession = createSelector(
    selectSessionState,
    (state: ISessionState): ISessionState => state
);
/**
 * Returns currently logged-in user
 * @param state
 */
export const getUser = createSelector(
    selectSessionState,
    (state: ISessionState): User =>  state.user
);

/**
 * Returns currently logged-in company
 * @param state
 */
export const getCompany = createSelector(
    selectSessionState,
    (state: ISessionState): Company =>  state.company
);

/**
 * Returns the skill sets.
 * @param state
 * @returns {any[]}
 */
export const getSkillSets = createSelector(
    selectSessionState,
    (state: ISessionState) => state.skillSets
);

export const getCompanySkills = createSelector(
    [getSkillSets, getCompany],
    (skillSets: any, company: any) => {
        const skillSetIds = skillSets
            ? skillSets.map((x) => x.skill_set_id)
            : [];
        let companySkills: Skill[] = [];

        if (company && company.skills) {
            companySkills = company.skills
                .map((x) => {
                    if (x.title === null || x.title === '') {
                        const index = skillSetIds.indexOf(x.skill_set_id);
                        x.title = index > -1 ? skillSets[index].name : '';
                    }

                    return Object.assign(new Skill(), x);
                })
                .sort(sortAlphabeticalByProperty('title'));
        }

        return companySkills;
    }
);

export const getCompanyFeatures = createSelector(
    selectSessionState,
    (state: ISessionState) => state.companyFeatures
);

export const getSessionLocale = createSelector(
    selectSessionState,
    (state: ISessionState): string => state.locale
);

export const getPermissions = createSelector(
    selectSessionState,
    (state: ISessionState): any => state.permissions
);

export const getCountry = createSelector(
    selectSessionState,
    (state: ISessionState): any => state.country
);

export const getUserDatetimeFormats = createSelector(
    [getUserDatetimeOptions, getUser],
    (formats: DatetimeFormat[], sessionUser: User) => {
        const userFormatId: number =
            (sessionUser && sessionUser.datetime_format) || 0;
        const userFormats: DatetimeFormat = formats.find(
            (format) => format.id === userFormatId
        );

        return userFormats;
    }
);

export const getCompanyInstallDate = createSelector(
    selectSessionState,
    (state: ISessionState): moment.Moment => state.companyInstallDate
);

export const getCompanyStatus = createSelector(
    selectSessionState,
    (state: ISessionState): any => state.companyStatus
);

export const isSessionInitComplete = createSelector(
    selectSessionState,
    (state: ISessionState): boolean => state.initSessionComplete
);
